/**
 * "Answer it", under a gap reasoning cannot settle.
 *
 * A knowledge gap the model closed as "open: reasoning can't settle it" is a
 * question waiting on the person — and the commonest one is about scope:
 * "'Displace' is underspecified" can only be settled by whoever meant something
 * by "displace". The app already had the answer's home, the standing brief's
 * `/given` and `/constraint`, and no persona in the study found either: they
 * live behind a slash in the composer. So the gap carries its own door to them.
 *
 * What it writes is an ordinary directive, through the brief's ONE writer
 * (`setBrief`), with the gap's id beside it (`Directive.answers`) so the gap can
 * say it was answered and take the answer back. That makes it everything the
 * brief already is and nothing more: free — no model call, no lock — undoable as
 * one step of history, visible among the composer's brief chips, and read by
 * every later pass under its kind. A given stays stipulated, never certified:
 * the pass is told to reason inside it and never to call it true, and nothing
 * here gives it a confidence or a reading.
 *
 * The gap itself does not change. Its ending is still the model's ("open"), and
 * `prune` never offers an open gap, answered or not (`PRUNE_ENDINGS`); the
 * answer is the person's, on the brief, where they can see and remove it.
 */

import { useEffect, useRef, useState } from 'react'
import { useStore } from '../core/store'
import { briefAnswerOf, endingOf } from '../core/endings'
import { DIRECTIVE_CHARS, briefRefusal } from '../core/brief'
import { DIRECTIVE_LABEL, type Directive, type ReasonNode, type Session } from '../core/types'
import { STATUS_WORD } from '../core/vocabulary'

/** The two kinds a scope question is answered with. An exclusion or a criterion
 *  answers a different question — what not to explore, what would settle it —
 *  and stays in the composer's hands. */
type AnswerKind = Extract<Directive['kind'], 'given' | 'constraint'>

/** What each kind does to every later pass, in the composer's own words for it. */
const KIND_NOTE: Record<AnswerKind, string> = {
  given:
    'A premise you stipulate: every pass reasons inside it and none argues with it — stipulated, never certified.',
  constraint:
    'A bound you actually have: a route it kills says so, and comes back if you relax it.',
}

const PLACEHOLDER: Record<AnswerKind, string> = {
  given: 'What you mean by it, or the case you care about, in your own words.',
  constraint: 'The bound you actually have — a budget, a date, a place, a scale.',
}

/** The brief codec's own clamp on a directive's words (`asBrief`), said at the
 *  field so nothing typed is cut on the way to disk without warning. */
const ANSWER_CHARS = DIRECTIVE_CHARS

export function GapAnswer({ session, node }: { session: Session; node: ReasonNode }) {
  const setBrief = useStore((s) => s.setBrief)
  const toast = useStore((s) => s.toast)
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<AnswerKind>('given')
  const [words, setWords] = useState('')

  // A new node is a new question.
  useEffect(() => {
    setOpen(false)
    setWords('')
    setKind('given')
  }, [node.id])

  // Each of the three presses here takes itself away — Cancel and Add close the
  // editor, Take it back ends the answer — and left the key that pressed it on
  // the page body. So focus goes to what replaced it (D217's rule): Answer it,
  // or Take it back once an answer stands. Only when focus really was lost, and
  // never on a change nobody here pressed (another node, an Undo in a toast).
  const answered = endingOf(node) === 'open' && !!briefAnswerOf(session, node.id)
  const shape = endingOf(node) !== 'open' ? null : answered ? 'answered' : open ? 'open' : 'closed'
  const pressed = useRef(false)
  const answerLink = useRef<HTMLButtonElement | null>(null)
  const takeBack = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (!pressed.current) return
    pressed.current = false
    const here = document.activeElement
    if (here && here !== document.body) return
    const to =
      shape === 'answered' ? takeBack.current : shape === 'closed' ? answerLink.current : null
    to?.focus({ preventScroll: true })
  }, [shape])

  // Only on a gap whose ending is open: the model closed it because reasoning
  // cannot settle it. A result that answered it has made it `answered` instead.
  if (endingOf(node) !== 'open') return null

  const answer = briefAnswerOf(session, node.id)
  const brief = session.brief ?? []

  if (answer) {
    return (
      <p className="ncard-kin">
        <span>
          {STATUS_WORD.answeredInBrief} — {DIRECTIVE_LABEL[answer.kind]}: “{answer.text}”
          {answer.kind === 'given' ? ' · stipulated, never certified' : ''}
        </span>
        <span className="ncard-kin-acts">
          <button
            ref={takeBack}
            type="button"
            className="linkish"
            title="Takes it out of the standing brief, one step of history. No model call."
            onClick={() => {
              pressed.current = true
              setBrief(brief.filter((d) => d !== answer))
            }}
          >
            Take it back
          </button>
        </span>
      </p>
    )
  }

  // The brief's own sentence for a full list, the one the composer says too — it
  // named "a chip with a ✕ above the input", and a long brief is one chip there now.
  const text = words.trim()
  const full = briefRefusal(brief, [{ kind, text }])
  const add = () => {
    if (!text || full) return
    const directive: Directive = { kind, text: text.slice(0, ANSWER_CHARS), answers: node.id }
    pressed.current = true
    setBrief([...brief, directive])
    setOpen(false)
    setWords('')
    toast(
      `Added to your standing brief as a ${DIRECTIVE_LABEL[kind].toLowerCase()}. Every pass from here reasons inside it${kind === 'given' ? ' — stipulated, never certified' : ''}.`,
      'info',
      {
        label: 'Undo',
        run: () => {
          // Exactly the directive this press wrote, wherever it now sits —
          // never "the last step", which may since be somebody else's.
          const cur = useStore.getState()
          const now = cur.session.brief ?? []
          if (now.some((d) => d.answers === node.id && d.text === directive.text))
            cur.setBrief(now.filter((d) => !(d.answers === node.id && d.text === directive.text)))
        },
      },
    )
  }

  if (!open) {
    return (
      <p className="ncard-kin">
        <span className="ncard-kin-acts">
          <button
            ref={answerLink}
            type="button"
            className="linkish"
            title="Write your answer as a given or a constraint in this exploration's standing brief. No model call; undoable."
            onClick={() => setOpen(true)}
          >
            Answer it
          </button>
        </span>
        <span>as a given or a constraint every later pass works inside · free</span>
      </p>
    )
  }

  return (
    <div className="editbox">
      <div className="tabs" role="radiogroup" aria-label="Answer it as">
        {(['given', 'constraint'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            onClick={() => setKind(k)}
          >
            {DIRECTIVE_LABEL[k]}
          </button>
        ))}
      </div>
      <textarea
        className="textarea"
        rows={3}
        maxLength={ANSWER_CHARS}
        value={words}
        onChange={(e) => setWords(e.target.value)}
        placeholder={PLACEHOLDER[kind]}
        aria-label={`Your answer, as a ${DIRECTIVE_LABEL[kind].toLowerCase()}`}
        // Opened by a press whose only purpose is to type here.
        autoFocus
      />
      <p className="gnote gnote--quiet">{KIND_NOTE[kind]}</p>
      {full && <p className="gnote gnote--warn">{full}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn--primary" disabled={!text || !!full} onClick={add}>
          Add to the standing brief
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            pressed.current = true
            setOpen(false)
          }}
        >
          Cancel
        </button>
        <span className="vp-spent">{STATUS_WORD.free}</span>
      </div>
    </div>
  )
}
